import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {Fade, Slide} from '@aboutyou/transitions';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {createPortal} from 'react-dom';
import {useDrawerPortalElement} from '../../../hooks/usePortalElement/useDrawerPortalElement';
import {tabletAndUp} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export const ANIMATION_DURATION = 450;

export interface DrawerProps extends ContentPopperBaseProps {
  slideDirection?: 'left' | 'right';
  OverlayComponent?: React.ComponentType<
    React.ComponentProps<typeof BlurredOverlay>
  >;
  ref?: React.RefObject<HTMLDivElement>;
}

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  function DrawerComponent(
    {
      className,
      dataTestId,
      headerContent,
      bodyContent,
      onClose,
      open,
      slideDirection = 'right',
      renderInPortal,
      OverlayComponent = BlurredOverlay,
      showHeaderDivider = true,
    },
    ref,
  ) {
    useLockBodyScroll({lockScroll: open});

    const drawerContent = (
      <>
        <Fade mounted={open} duration={ANIMATION_DURATION}>
          <OverlayComponent
            $zIndex={Z_INDEXES.OFF_CANVAS}
            data-testid="drawerBackdrop"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
          />
        </Fade>
        <Slide
          mounted={open}
          direction={slideDirection}
          duration={ANIMATION_DURATION}
        >
          <FixedContainer
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
            visible={open}
            className={className}
            $direction={slideDirection}
          >
            <DrawerContainer data-testid={dataTestId} ref={ref}>
              <DrawerHeaderContainer $showHeaderDivider={showHeaderDivider}>
                <DrawerHeaderContent>{headerContent}</DrawerHeaderContent>
                <CloseButton onClick={onClose} data-testid="drawerCloseIcon">
                  <Icon asset={GenericIconName.Close} size="S" />
                </CloseButton>
              </DrawerHeaderContainer>
              {Array.isArray(bodyContent) ? (
                bodyContent
                  .filter(section => !!section)
                  .map((bodySection, key) => {
                    return (
                      <React.Fragment key={key}>{bodySection}</React.Fragment>
                    );
                  })
              ) : (
                <DrawerBody>{bodyContent}</DrawerBody>
              )}
            </DrawerContainer>
          </FixedContainer>
        </Slide>
      </>
    );

    if (renderInPortal) {
      return <RenderInPortal>{drawerContent}</RenderInPortal>;
    }

    return <>{drawerContent}</>;
  },
);

const RenderInPortal: React.FC<React.PropsWithChildren> = ({children}) => {
  const drawerPortalElement = useDrawerPortalElement();

  if (!drawerPortalElement) {
    return null;
  }

  return createPortal(<>{children}</>, drawerPortalElement);
};

const DrawerHeaderContent = styled.div`
  height: 100%;
  flex-grow: 1;
  overflow: hidden;
  padding-left: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const DrawerContainer = styled.section`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;

  ${tabletAndUp(css`
    width: 400px;
  `)}
`;

export const DrawerBody = styled.section`
  border-top: 1px solid ${COLORS.Grey3};
  flex: 1 1 auto;
  padding: 0 20px 10px 20px;
`;

const DrawerHeaderContainer = styled.header<{$showHeaderDivider: boolean}>`
  height: 64px;
  display: flex;
  flex: 0 0 56px;
  background-color: ${COLORS.White};
  position: sticky;
  top: 0;
  z-index: ${Z_INDEXES.APP_SCROLL_TO_TOP};

  ${({$showHeaderDivider}) =>
    $showHeaderDivider &&
    css`
      box-shadow: 0 1px 0 0 ${COLORS.Grey3}; // box-shadow is used as a collapsible border
    `}
`;

const CloseButton = styled(UnstyledButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FixedContainer = styled.aside<{
  visible: boolean;
  $direction: 'left' | 'right';
}>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${({visible}) => (visible ? 'all' : 'none')};
  touch-action: none;
  overflow: clip;
  z-index: ${Z_INDEXES.DRAWER};

  ${({$direction}) =>
    $direction === 'left'
      ? css`
          right: 0;
        `
      : css`
          left: 0;
        `}

  ${({$direction}) =>
    tabletAndUp(css`
      max-width: 400px;

      ${$direction === 'left'
        ? css`
            border-radius: 10px 0 0 10px;
          `
        : css`
            border-radius: 0 10px 10px 0;
          `}
    `)}
`;
